import { isSameDay, setHours, startOfDay } from "date-fns";
import { type DeadlineShortening, type EstimateResult } from "../types";
import {
  addBusinessDays,
  countBusinessDays,
  getReceptionDate,
  RECEPTION_CUTOFF_HOUR,
} from "./businessDays";

const MAX_SHORTENING = 3;

export interface DeliveryScheduleInput {
  /** 標準納期（「5営業日」または「要相談」） */
  standardDelivery: string;
  /** 依頼する時刻（ふつうは今） */
  now: Date;
  deadlineShortening: DeadlineShortening;
  specifyDeliveryDate: boolean;
  /** yyyy-MM-dd。日付を指定しないときは空 */
  desiredDeliveryDate: string;
}

export interface DeliverySchedule {
  /** 標準の納期。今日の15時までに受け付けたときと、15時以降に受け付けたとき */
  standardDates: { beforeCutoff: Date; afterCutoff: Date };
  /** 選んだ短縮を反映した納期（日付を指定したときは標準と同じ） */
  shortenedDates: { beforeCutoff: Date; afterCutoff: Date };
  /** 今受け付けたときの標準の納期 */
  standardDate: Date;
  /** 短縮・日付の指定を反映した納期。個別見積なら null */
  expectedDate: Date | null;
  /** 見積に使う短縮の営業日数。日付を指定したときは、その日に間に合わせるのに要る日数 */
  shortening: DeadlineShortening;
  /** 指定した日付が3営業日短縮より早く、納期と金額を個別に見積もる */
  quoteRequired: boolean;
}

const parseBusinessDays = (standardDelivery: string): number | null => {
  const days = /^(\d+)営業日$/.exec(standardDelivery)?.[1];
  return days === undefined ? null : Number(days);
};

const toShortening = (days: number): DeadlineShortening =>
  Math.min(Math.max(days, 0), MAX_SHORTENING) as DeadlineShortening;

/**
 * 標準納期と選んだ短縮・日付から、納期の日付と見積に使う短縮を求める。
 * 標準納期が要相談のときは日付を出せないため null を返す。
 */
export const getDeliverySchedule = (
  input: DeliveryScheduleInput
): DeliverySchedule | null => {
  const standardDays = parseBusinessDays(input.standardDelivery);
  if (standardDays === null) return null;

  const today = startOfDay(input.now);
  const receptionBeforeCutoff = getReceptionDate(today);
  const receptionAfterCutoff = getReceptionDate(
    setHours(today, RECEPTION_CUTOFF_HOUR)
  );
  const receptionNow = getReceptionDate(input.now);
  const datesFor = (
    days: number
  ): { beforeCutoff: Date; afterCutoff: Date } => ({
    beforeCutoff: addBusinessDays(receptionBeforeCutoff, days),
    afterCutoff: addBusinessDays(receptionAfterCutoff, days),
  });
  const standardDate = addBusinessDays(receptionNow, standardDays);

  if (input.specifyDeliveryDate && input.desiredDeliveryDate) {
    const desiredDate = startOfDay(
      new Date(`${input.desiredDeliveryDate}T00:00:00`)
    );
    // 標準納期以降の日付は短縮が要らない。数え上げは標準納期までに限り、遠い日付でも回数を増やさない
    const neededDays =
      desiredDate >= standardDate
        ? 0
        : standardDays - countBusinessDays(receptionNow, desiredDate);
    const quoteRequired = neededDays > MAX_SHORTENING;
    return {
      standardDates: datesFor(standardDays),
      shortenedDates: datesFor(standardDays),
      standardDate,
      expectedDate: quoteRequired ? null : desiredDate,
      shortening: quoteRequired ? 0 : toShortening(neededDays),
      quoteRequired,
    };
  }

  const shortening = input.specifyDeliveryDate ? 0 : input.deadlineShortening;
  return {
    standardDates: datesFor(standardDays),
    shortenedDates: datesFor(standardDays - shortening),
    standardDate,
    expectedDate: addBusinessDays(receptionNow, standardDays - shortening),
    shortening,
    quoteRequired: false,
  };
};

interface Quote {
  delivery: DeliverySchedule | null;
  estimate: EstimateResult;
}

/** 依頼者に見せた納期・金額と、送る直前に計算し直したものが違うか */
export const hasQuoteChanged = (shown: Quote, latest: Quote): boolean => {
  const shownDate = shown.delivery?.expectedDate ?? null;
  const latestDate = latest.delivery?.expectedDate ?? null;
  const sameDate =
    shownDate === null || latestDate === null
      ? shownDate === latestDate
      : isSameDay(shownDate, latestDate);
  return (
    !sameDate ||
    shown.estimate.total !== latest.estimate.total ||
    shown.estimate.deliveryQuoteRequired !==
      latest.estimate.deliveryQuoteRequired ||
    shown.estimate.shorteningRate !== latest.estimate.shorteningRate
  );
};
